import React, { useState, useContext, useMemo, useRef, useLayoutEffect, useEffect } from 'react'
import { useQuery, useMutation, useLazyQuery } from '@apollo/client'
import UserContext from '../context/UserContext'
import languages, { getLanguageAcronym } from '../languages'
import useLearnerContext from '../hooks/useLearnerContext'
import { isOwnerRole } from '../util/roles'
import {
  GET_LANGUAGE_TREE_QUERY, SEED_CURRICULUM_MUTATION, COMPLETE_TOPIC_MUTATION,
  ATTACH_LESSON_TO_TOPIC_MUTATION, ADD_MEETING_WITH_CONTENT_MUTATION, GET_MEETING_WITH_CONTENT_QUERY, ME_QUERY,
} from '../query/gqlQueries'
import { streamOllamaGenerate } from '../ollama'
import StudentLessonView from './StudentLessonView'
import curricula from '../data/curricula'
import { getBuiltinLesson } from '../data/lessons'
import { buildTopicLesson, parseObj } from '../lessons/buildTopicLesson'

import CloseIcon from '@mui/icons-material/Close'
import LockIcon from '@mui/icons-material/Lock'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import PlayArrowIcon from '@mui/icons-material/PlayArrow'
import LightModeIcon from '@mui/icons-material/LightMode'
import DarkModeIcon from '@mui/icons-material/DarkMode'

// Light / dark, shared with the lesson, home, reader and partner views through
// the same key. This view has no stylesheet of its own — it is inline styles
// plus one <style> block — so the palette lives here and is published to the
// CSS as custom properties on the root, which the <style> block reads.
const THEME_KEY = 'lessonTheme'
const readTheme = () => { try { const t = localStorage.getItem(THEME_KEY); return t === 'light' || t === 'dark' ? t : 'dark' } catch { return 'dark' } }

const PALETTE = {
  dark: {
    bg: '#0b0b0d', bgGrad: '#16161c', bgFade: 'rgba(11,11,13,0.7)',
    surface: '#141418', surface2: '#1b1b21', closeBg: '#23232b',
    node: '#111116', nodeLocked: '#0e0e12', doneTop: '#15241f', panel: '#101014',
    border: '#2a2a33', borderSoft: '#24242b', borderDash: '#3a3a42', panelBorder: '#26262c',
    divider: 'rgba(42,42,51,0.55)', link: '#3a3a44',
    text: '#e8e8ec', muted: '#8a8a95', blurb: '#8f8f9a', dim: '#5a5a65',
    dimmer: '#6f6f7a', dimmest: '#47474f', needs: '#6a6a75',
    accent: '#6ee7b7', accent2: '#34d399', onAccent: '#06281d',
    accentRgb: '52,211,153', glowRgb: '45,212,191', brightRgb: '110,231,183',
    danger: '#f87171', amber: '#fbbf24', overlay: 'rgba(0,0,0,0.5)',
  },
  light: {
    bg: '#FAF6EF', bgGrad: '#F4ECE1', bgFade: 'rgba(250,246,239,0.7)',
    surface: '#FFFFFF', surface2: '#F4ECE1', closeBg: '#F4ECE1',
    node: '#FFFFFF', nodeLocked: '#F1EDE6', doneTop: '#EBF0E9', panel: '#F4ECE1',
    border: '#E7E5E4', borderSoft: '#EDEAE5', borderDash: '#D6D3D1', panelBorder: '#E7E5E4',
    divider: 'rgba(120,113,108,0.3)', link: '#CFCAC3',
    text: '#22201D', muted: '#5A5650', blurb: '#5A5650', dim: '#8C867E',
    dimmer: '#A8A29B', dimmest: '#B5AFA7', needs: '#8C867E',
    accent: '#3B4D3A', accent2: '#3B4D3A', onAccent: '#FFFFFF',
    accentRgb: '59,77,58', glowRgb: '59,77,58', brightRgb: '59,77,58',
    danger: '#C15C3D', amber: '#C88A2B', overlay: 'rgba(34,32,29,0.35)',
  },
}
// The custom properties the <style> block below reads, set on the root element.
const cssVars = (P) => ({
  '--lt-bg': P.bg, '--lt-bg-fade': P.bgFade, '--lt-node': P.node, '--lt-node-locked': P.nodeLocked,
  '--lt-done-top': P.doneTop, '--lt-border-soft': P.borderSoft, '--lt-divider': P.divider,
  '--lt-text': P.text, '--lt-blurb': P.blurb, '--lt-dim': P.dim, '--lt-dimmer': P.dimmer,
  '--lt-dimmest': P.dimmest, '--lt-needs': P.needs, '--lt-accent': P.accent, '--lt-accent2': P.accent2,
  '--lt-accent-rgb': P.accentRgb, '--lt-glow-rgb': P.glowRgb, '--lt-bright-rgb': P.brightRgb,
})

const LEVELS = ['A1', 'A2', 'B1', 'B2', 'C1', 'C2']
const LEVEL_NAMES = { A1: 'Beginner', A2: 'Elementary', B1: 'Intermediate', B2: 'Upper Int.', C1: 'Advanced', C2: 'Mastery' }
const S = (v) => String(v || '').trim()
// initialLanguage / initialSlug: the dashboard opens the tree on a language and
// (optionally) straight onto one topic's modal.
export default function LanguageTreeView({ voiceSpeed, onClose, initialLanguage, initialSlug }) {
  const { username } = useContext(UserContext)
  const { learning, personalize } = useLearnerContext(username)
  const personalKey = (t) => `lt-personal-${code}-${t.slug}`
  const readPersonal = (t) => { try { return localStorage.getItem(personalKey(t)) } catch { return null } }
  const learningNames = (learning || []).map(l => l.name).filter(Boolean)

  const [langName, setLangName] = useState(initialLanguage || '')
  const effName = langName || learningNames[0] || 'Turkish'
  const code = getLanguageAcronym(effName) || 'tur'

  // errorPolicy 'all' (not 'ignore'): a failing query must surface, otherwise a
  // backend that hasn't shipped the Topic schema looks like an empty tree.
  const { data, loading, refetch, error: treeError } = useQuery(GET_LANGUAGE_TREE_QUERY, {
    variables: { langCode: code }, skip: !username, fetchPolicy: 'cache-and-network', errorPolicy: 'all',
  })
  const topics = data?.getLanguageTree || []
  const backendOutOfDate = !!treeError && /Cannot query field|Unknown type/i.test(treeError.message || '')
  // Only the owner may author / reseed the shared tree; students just climb it.
  const { data: meData } = useQuery(ME_QUERY, { errorPolicy: 'ignore' })
  const isOwner = isOwnerRole(meData?.me?.role)

  const [seedCurriculum] = useMutation(SEED_CURRICULUM_MUTATION)
  const [completeTopic] = useMutation(COMPLETE_TOPIC_MUTATION)
  const [attachLesson] = useMutation(ATTACH_LESSON_TO_TOPIC_MUTATION)
  const [addMeeting] = useMutation(ADD_MEETING_WITH_CONTENT_MUTATION)
  const [fetchMeeting] = useLazyQuery(GET_MEETING_WITH_CONTENT_QUERY, { fetchPolicy: 'network-only' })

  const [busy, setBusy] = useState('')
  const [error, setError] = useState('')
  const [selected, setSelected] = useState(null)   // topic node in the modal
  const [lesson, setLesson] = useState(null)       // { lesson, meetingId } → StudentLessonView

  // Opened from the dashboard on a specific topic: pop its modal once the tree
  // has loaded — once only, so closing the modal doesn't reopen it.
  const focusedSlugRef = useRef(null)
  useEffect(() => {
    if (!initialSlug || focusedSlugRef.current === initialSlug || !topics.length) return
    const t = topics.find(x => x.slug === initialSlug)
    if (t) { focusedSlugRef.current = initialSlug; setSelected(t) }
  }, [initialSlug, topics])

  const completedSlugs = useMemo(() => new Set(topics.filter(t => t.completed).map(t => t.slug)), [topics])
  const stateOf = (t) => t.completed ? 'done' : ((t.requires || []).every(r => completedSlugs.has(r)) ? 'open' : 'locked')
  const titleBySlug = useMemo(() => Object.fromEntries(topics.map(t => [t.slug, t.title])), [topics])

  // Hover a card → light up only its prerequisite chain and its unlocks, fade
  // the rest. Keeps a dense tree calm instead of drawing every edge at once.
  const [hovered, setHovered] = useState(null)
  const [theme, setTheme] = useState(readTheme)
  const P = PALETTE[theme] || PALETTE.dark
  const toggleTheme = () => setTheme(t => {
    const next = t === 'dark' ? 'light' : 'dark'
    try { localStorage.setItem(THEME_KEY, next) } catch { /* private mode */ }
    return next
  })
  const dependents = useMemo(() => {
    const m = {}
    topics.forEach(t => (t.requires || []).forEach(r => { (m[r] = m[r] || []).push(t.slug) }))
    return m
  }, [topics])
  const neighborSet = useMemo(() => {
    if (!hovered) return null
    const t = topics.find(x => x.slug === hovered)
    const s = new Set([hovered])
    ;(t?.requires || []).forEach(r => s.add(r))
    ;(dependents[hovered] || []).forEach(d => s.add(d))
    return s
  }, [hovered, topics, dependents])

  const byLevel = useMemo(() => {
    const m = {}
    topics.forEach(t => { const lv = t.level || 'A1'; (m[lv] = m[lv] || []).push(t) })
    Object.values(m).forEach(a => a.sort((x, y) => (x.order || 0) - (y.order || 0)))
    return m
  }, [topics])
  const levelsPresent = LEVELS.filter(l => byLevel[l]?.length)

  // ── prerequisite arrows: measured after layout, drawn in an SVG overlay ─────
  const treeRef = useRef(null)
  const nodeRefs = useRef({})
  const [links, setLinks] = useState([])
  const [svgSize, setSvgSize] = useState({ w: 0, h: 0 })
  useLayoutEffect(() => {
    const compute = () => {
      const wrap = treeRef.current
      if (!wrap) return
      const wr = wrap.getBoundingClientRect()
      const out = []
      topics.forEach(t => (t.requires || []).forEach(r => {
        const a = nodeRefs.current[r], b = nodeRefs.current[t.slug]
        if (!a || !b) return
        const ra = a.getBoundingClientRect(), rb = b.getBoundingClientRect()
        const sameCol = Math.abs(ra.left - rb.left) < 10
        const x1 = sameCol ? ra.left - wr.left + ra.width / 2 : ra.right - wr.left
        const y1 = sameCol ? ra.bottom - wr.top : ra.top - wr.top + ra.height / 2
        const x2 = sameCol ? rb.left - wr.left + rb.width / 2 : rb.left - wr.left
        const y2 = sameCol ? rb.top - wr.top : rb.top - wr.top + rb.height / 2
        const locked = !(t.requires || []).every(q => completedSlugs.has(q))
        out.push({ key: `${r}>${t.slug}`, from: r, to: t.slug, x1, y1, x2, y2, sameCol, done: completedSlugs.has(r), locked })
      }))
      setLinks(out)
      setSvgSize({ w: wrap.scrollWidth, h: wrap.scrollHeight })
    }
    compute()
    window.addEventListener('resize', compute)
    return () => window.removeEventListener('resize', compute)
  }, [topics, completedSlugs])

  const serverUrl = process.env.REACT_APP_OLLAMA_URI || 'http://localhost:11434'
  const apiKey = process.env.REACT_APP_OLLAMA_KEY

  // ── AI-seed the curriculum for this language ─────────────────────────────────
  const generateCurriculum = async () => {
    setError('')
    // Prefer the hand-authored canonical tree (src/data/curricula.js); fall back
    // to the local model only for a language we haven't written one for.
    const builtIn = curricula[code]
    if (builtIn) {
      setBusy('Loading the curriculum…')
      try {
        await seedCurriculum({ variables: { langCode: code, name: builtIn.name || effName, topics: builtIn.topics } })
        await refetch()
      } catch (e) { setError(e.message || 'Could not load the curriculum.') }
      setBusy('')
      return
    }
    setBusy('Designing the tree…')
    const prompt = `Design a beginner→intermediate SKILL TREE for learning ${effName}, like a game tech tree. Produce 16-22 topics across CEFR A1, A2 and B1. Each topic is ONE concrete grammar or vocabulary unit a student unlocks in order.
For each: slug (kebab-case unique id), title (short), level ("A1"|"A2"|"B1"), order (1-based within its level), blurb (one short line), requires (array of slugs that MUST be done first — earlier topics only; the very first topics have []).
Prerequisites must reference ONLY slugs present in your list and form a sensible progression (basics before advanced).
Return ONLY minified JSON: {"topics":[{"slug":"","title":"","level":"","order":1,"blurb":"","requires":[]}]}`
    try {
      const raw = await streamOllamaGenerate({
        serverUrl, apiKey, body: { model: 'qwen2.5:14b', prompt, format: 'json', options: { temperature: 0.4, num_predict: 2600 } },
        onProgress: (acc) => setBusy(`Designing the tree… (${acc.length} chars)`),
      })
      const o = parseObj(raw)
      const list = (Array.isArray(o?.topics) ? o.topics : []).map(t => ({
        slug: S(t?.slug).toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, ''),
        title: S(t?.title), level: LEVELS.includes(t?.level) ? t.level : 'A1',
        order: Number.isFinite(t?.order) ? t.order : 0, blurb: S(t?.blurb),
        requires: Array.isArray(t?.requires) ? t.requires.map(s => S(s).toLowerCase().replace(/[^a-z0-9]+/g, '-')) : [],
      })).filter(t => t.slug && t.title)
      const slugs = new Set(list.map(t => t.slug))
      list.forEach(t => { t.requires = t.requires.filter(r => slugs.has(r) && r !== t.slug) }) // drop dangling prereqs
      if (list.length === 0) { setError('The model returned no topics — try again.'); setBusy(''); return }
      await seedCurriculum({ variables: { langCode: code, name: effName, topics: list } })
      await refetch()
    } catch (e) {
      setError((e.message || '').match(/Failed to fetch|Load failed/) ? 'Connection to the Mac Studio failed.' : (e.message || 'Generation failed.'))
    }
    setBusy('')
  }

  // Prompt for a topic lesson. `personal` is null for the shared/generic
  // (canonical) lesson, or a personalisation brief for a private one.
  // Workbook-style prompt (the structure of a good teacher's handout). When a
  // hand-authored canonical lesson exists it is passed as `exemplar` so the
  // model ADAPTS a correct lesson instead of inventing one from scratch.
  // Generate + save a lesson meeting. Returns { lessonObj, meetingId }.
  // `prebuilt` = a hand-authored lesson (src/data/lessons) → saved as-is, no
  // model call. Otherwise the model writes one; if an authored lesson exists it
  // is handed over as the exemplar so a personalised copy keeps its quality.
  const buildLessonMeeting = (t, personal) => buildTopicLesson({
    topic: t, code, langName: effName, username, personal, addMeeting,
    onProgress: (acc) => setBusy(`${personal ? 'Tailoring' : 'Building'} the lesson… (${acc.length} chars)`),
  })

  const openSavedMeeting = async (meetingId, t, msg) => {
    setBusy(msg || 'Loading lesson…')
    try {
      const res = await fetchMeeting({ variables: { identity: meetingId } })
      const parsed = parseObj(res.data?.getMeetingWithContent?.meeting?.properties?.lessonData)
      if (parsed) { setSelected(null); setLesson({ lesson: parsed, meetingId, topicId: t.id, completed: t.completed }); return true }
    } catch (e) { setError(e.message) }
    setBusy('')
    return false
  }

  // Open the shared canonical lesson. `force` (owner) rebuilds a fresh generic
  // one and REPLACES the attached lesson — used to refresh / de-personalise.
  const openTopic = async (t, force = false) => {
    setError('')
    if (!force && t.lessonMeetingId) {
      if (await openSavedMeeting(t.lessonMeetingId, t)) return
      setError('This lesson has no content — an owner can regenerate it.'); setBusy(''); return
    }
    // Prefer the hand-authored canonical lesson (instant, correct, offline);
    // fall back to the local model only for topics nobody has written yet.
    const builtin = getBuiltinLesson(code, t.slug)
    setBusy(builtin ? 'Loading the lesson…' : (force ? 'Rebuilding the shared lesson…' : 'Building the lesson…'))
    try {
      const { lessonObj, meetingId } = await buildLessonMeeting(t, null)   // generic, shared
      if (meetingId) { await attachLesson({ variables: { topicId: t.id, meetingId } }); await refetch() }
      setSelected(null); setLesson({ lesson: lessonObj, meetingId, topicId: t.id, completed: t.completed })
    } catch (e) {
      setError((e.message || '').match(/Failed to fetch|Load failed/) ? 'Connection to the Mac Studio failed.' : (e.message || 'Generation failed.'))
    }
    setBusy('')
  }

  // A private, tailored copy — generated with the learner's profile, saved as
  // THEIR meeting, remembered locally, and NEVER attached to the shared topic.
  const personaliseTopic = async (t) => {
    setError(''); setBusy('Tailoring your lesson…')
    try {
      const { lessonObj, meetingId } = await buildLessonMeeting(t, personalize(effName) || 'The learner has no profile details yet — keep it generic but friendly.')
      if (meetingId) { try { localStorage.setItem(personalKey(t), meetingId) } catch { /* private */ } }
      setSelected(null); setLesson({ lesson: lessonObj, meetingId, topicId: t.id, completed: t.completed })
    } catch (e) {
      setError((e.message || '').match(/Failed to fetch|Load failed/) ? 'Connection to the Mac Studio failed.' : (e.message || 'Generation failed.'))
    }
    setBusy('')
  }
  const openMyPersonal = async (t) => {
    const id = readPersonal(t)
    if (!id) return personaliseTopic(t)
    setError('')
    const ok = await openSavedMeeting(id, t, 'Loading your version…')
    if (!ok) { try { localStorage.removeItem(personalKey(t)) } catch { /* */ }; personaliseTopic(t) }
  }

  const markComplete = async (t) => {
    try { await completeTopic({ variables: { topicId: t.id } }); await refetch(); setSelected(null) }
    catch (e) { setError(e.message) }
  }

  // ── the opened lesson replaces the tree ──────────────────────────────────────
  if (lesson) {
    const finishTopic = async () => {
      if (lesson.topicId && !lesson.completed) { try { await completeTopic({ variables: { topicId: lesson.topicId } }) } catch (e) { /* non-fatal */ } }
      setLesson(null); refetch()
    }
    return <StudentLessonView lesson={lesson.lesson} meetingId={lesson.meetingId} voiceSpeed={voiceSpeed}
      onComplete={lesson.topicId ? finishTopic : undefined} completed={lesson.completed}
      onClose={() => { setLesson(null); refetch() }} />
  }

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 1300, background: `radial-gradient(1200px 600px at 50% -10%, ${P.bgGrad}, ${P.bg})`, color: P.text, display: 'flex', flexDirection: 'column', fontFamily: "'Inter', system-ui, sans-serif", transition: 'background .2s, color .2s', ...cssVars(P) }}>
      <button onClick={toggleTheme} title={theme === 'dark' ? 'Light mode' : 'Dark mode'} style={{ position: 'absolute', top: 14, right: 64, zIndex: 5, background: P.closeBg, border: `1px solid ${P.border}`, color: P.muted, borderRadius: 10, width: 40, height: 40, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}>
        {theme === 'dark' ? <LightModeIcon /> : <DarkModeIcon />}
      </button>
      <button onClick={onClose} style={{ position: 'absolute', top: 14, right: 16, zIndex: 5, background: P.closeBg, border: `1px solid ${P.border}`, color: P.text, borderRadius: 10, width: 40, height: 40, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}><CloseIcon /></button>

      <header style={{ padding: '1.3rem 1.6rem 0.8rem' }}>
        <div style={{ fontSize: '0.72rem', letterSpacing: '0.12em', textTransform: 'uppercase', color: P.muted }}>Skill Tree</div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap', marginTop: '0.3rem' }}>
          <h1 style={{ margin: 0, fontSize: '1.6rem', fontWeight: 700 }}>Your {effName} tree</h1>
          <select value={effName} onChange={e => setLangName(e.target.value)} style={{ background: P.surface, border: `1px solid ${P.border}`, color: P.text, borderRadius: 8, padding: '0.4rem 0.7rem' }}>
            {(learningNames.length ? learningNames : Object.values(languages)).map(n => <option key={n} value={n}>{n}</option>)}
          </select>
          <span style={{ color: P.muted, fontSize: '0.85rem' }}>{topics.filter(t => t.completed).length} / {topics.length} done</span>
        </div>
      </header>

      {error && <div style={{ color: P.danger, padding: '0 1.6rem 0.5rem' }}>{error}</div>}
      {busy && <div style={{ color: P.accent, padding: '0 1.6rem 0.5rem', fontSize: '0.9rem' }}>{busy}</div>}

      <main style={{ flex: 1, overflow: 'auto', padding: '0.6rem 2.2rem 2.5rem' }}>
        {loading && topics.length === 0 ? (
          <div style={{ color: P.muted, padding: '2rem' }}>Loading…</div>
        ) : backendOutOfDate ? (
          <div style={{ textAlign: 'center', padding: '3rem 1rem', maxWidth: 520, margin: '0 auto' }}>
            <div style={{ fontSize: '2.4rem' }}>⚠️</div>
            <h2 style={{ margin: '0.6rem 0' }}>The server hasn't been updated yet</h2>
            <p style={{ color: P.muted }}>The GraphQL server is running a version without the Skill Tree schema. Redeploy / restart it, then reopen this view.</p>
            <code style={{ color: P.danger, fontSize: '0.8rem' }}>{treeError.message}</code>
          </div>
        ) : topics.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '3rem 1rem', maxWidth: 460, margin: '0 auto' }}>
            <div style={{ fontSize: '2.4rem' }}>🌱</div>
            <h2 style={{ margin: '0.6rem 0' }}>No tree yet for {effName}</h2>
            {isOwner ? (
              <>
                <p style={{ color: P.muted }}>{curricula[code]
                  ? 'Load the canonical curriculum — the shared tree every learner of this language climbs. You can refine it afterwards.'
                  : 'No hand-written tree for this language yet — generate a starter with the local model, then refine it.'}</p>
                <button onClick={generateCurriculum} disabled={!!busy} style={{ marginTop: '1rem', background: P.accent2, color: P.onAccent, border: 'none', borderRadius: 10, padding: '0.7rem 1.5rem', fontWeight: 600, cursor: 'pointer' }}>
                  {curricula[code] ? '✦ Load curriculum' : '✦ Generate with AI'}
                </button>
              </>
            ) : (
              <p style={{ color: P.muted }}>Your teacher hasn't set up this tree yet — check back soon.</p>
            )}
          </div>
        ) : (
          <div ref={treeRef} className="lt-tree">
            <style>{`
              .lt-tree { position: relative; display: flex; gap: 11rem; align-items: flex-start; min-width: min-content; padding-top: 0.2rem; }
              .lt-col { flex: 0 0 auto; width: 268px; position: relative; }
              .lt-col + .lt-col::before { content: ''; position: absolute; left: -5.5rem; top: 3rem; bottom: 0; width: 1px; background: linear-gradient(180deg, transparent, var(--lt-divider) 8%, var(--lt-divider) 92%, transparent); }
              .lt-era { position: sticky; top: 0; z-index: 3; display: flex; align-items: baseline; gap: 0.55rem; padding: 0.4rem 0.1rem 0.6rem; margin-bottom: 1.4rem; background: linear-gradient(180deg, var(--lt-bg) 55%, var(--lt-bg-fade) 85%, transparent); backdrop-filter: blur(6px); }
              .lt-era b { font-size: 1.15rem; font-weight: 800; letter-spacing: 0.12em; color: var(--lt-accent); }
              .lt-era span { font-size: 0.72rem; text-transform: uppercase; letter-spacing: 0.1em; color: var(--lt-dim); }
              .lt-track { display: flex; flex-direction: column; gap: 3rem; }
              /* opaque base so prerequisite lines pass BEHIND cards and only show in the lanes */
              .lt-node { text-align: left; width: 100%; background: var(--lt-node); border: 1px solid; border-radius: 14px; padding: 1rem 1.1rem; cursor: pointer; position: relative; z-index: 1; color: var(--lt-text); transition: transform .13s ease, box-shadow .13s ease, border-color .13s ease; }
              .lt-node:hover { transform: translateY(-2px); }
              .lt-node:disabled { cursor: default; }
              .lt-node--done { background: linear-gradient(180deg, var(--lt-done-top), var(--lt-node)); border-color: rgba(var(--lt-accent-rgb),0.5); }
              .lt-node--open { border-color: var(--lt-accent2); animation: lt-pulse 2.8s ease-in-out infinite; }
              .lt-node--locked { background: var(--lt-node-locked); border-color: var(--lt-border-soft); color: var(--lt-dimmer); }
              .lt-node--locked:hover { transform: none; }
              /* border glows bright; the background wash stays muted */
              @keyframes lt-pulse {
                0%,100% { box-shadow: 0 0 0 1px rgba(var(--lt-accent-rgb),0.28), 0 0 12px rgba(var(--lt-glow-rgb),0.10); border-color: rgba(var(--lt-accent-rgb),0.65); background: linear-gradient(180deg, var(--lt-done-top), var(--lt-node)); }
                50%     { box-shadow: 0 0 0 1px rgba(var(--lt-bright-rgb),0.7), 0 0 26px rgba(var(--lt-glow-rgb),0.32); border-color: var(--lt-accent); background: linear-gradient(180deg, rgba(var(--lt-glow-rgb),0.13), var(--lt-node)); }
              }
              @media (prefers-reduced-motion: reduce) { .lt-node--open { animation: none; box-shadow: 0 0 0 1px rgba(var(--lt-accent-rgb),0.5); background: linear-gradient(180deg, rgba(var(--lt-glow-rgb),0.08), var(--lt-node)); } }
              /* bigger, roomier type — the cards were cramped and hard to scan */
              .lt-title { font-weight: 600; font-size: 1.06rem; line-height: 1.3; }
              .lt-blurb { font-size: 0.84rem; line-height: 1.45; margin-top: 0.35rem; color: var(--lt-blurb); }
              .lt-node--locked .lt-blurb { color: var(--lt-dimmest); }
              .lt-needs { font-size: 0.72rem; line-height: 1.35; margin-top: 0.5rem; color: var(--lt-needs); }
              .lt-dot { position: absolute; top: 0.7rem; right: 0.7rem; width: 7px; height: 7px; border-radius: 50%; background: var(--lt-accent2); box-shadow: 0 0 6px rgba(var(--lt-accent-rgb),0.6); }
            `}</style>
            <svg width={svgSize.w} height={svgSize.h} style={{ position: 'absolute', top: 0, left: 0, pointerEvents: 'none', zIndex: 0, overflow: 'visible' }}>
              <defs>
                {[['arrActive', P.accent]].map(([id, c]) => (
                  <marker key={id} id={id} viewBox="0 0 8 8" refX="7" refY="4" markerWidth="7" markerHeight="7" orient="auto"><path d="M0,0 L8,4 L0,8 z" fill={c} /></marker>
                ))}
              </defs>
              {links.map(l => {
                const active = hovered && (l.from === hovered || l.to === hovered)
                const dimmed = hovered && !active
                let d
                if (l.sameCol) {
                  d = `M ${l.x1} ${l.y1} C ${l.x1} ${l.y1 + 24}, ${l.x2} ${l.y2 - 24}, ${l.x2} ${l.y2}`
                } else {
                  const sx = l.x1 + 22, tx = l.x2 - 22, mid = (sx + tx) / 2   // horizontal stubs + flat S through the lane
                  d = `M ${l.x1} ${l.y1} H ${sx} C ${mid} ${l.y1}, ${mid} ${l.y2}, ${tx} ${l.y2} H ${l.x2}`
                }
                // Calm at rest: faint hairlines. On hover: the chain glows, the rest fades away.
                const stroke = active ? (l.done ? P.accent2 : P.accent) : (l.done ? `rgba(${P.accentRgb},0.5)` : P.link)
                const op = active ? 0.95 : dimmed ? 0.04 : (l.done ? 0.5 : 0.14)
                const sw = active ? 2 : 1
                return <path key={l.key} d={d} stroke={stroke} strokeWidth={sw} fill="none" markerEnd={active ? 'url(#arrActive)' : undefined} opacity={op} style={{ transition: 'opacity .15s' }} />
              })}
            </svg>
            {levelsPresent.map(lv => (
              <div key={lv} className="lt-col">
                <div className="lt-era"><b>{lv}</b><span>{LEVEL_NAMES[lv]}</span></div>
                <div className="lt-track">
                  {byLevel[lv].map(t => {
                    const st = stateOf(t)
                    return (
                      <button key={t.id} onClick={() => setSelected(t)} disabled={!!busy}
                        onMouseEnter={() => setHovered(t.slug)} onMouseLeave={() => setHovered(null)}
                        ref={el => { if (el) nodeRefs.current[t.slug] = el }}
                        className={`lt-node lt-node--${st}`}
                        style={{ opacity: neighborSet && !neighborSet.has(t.slug) ? 0.3 : 1, transition: 'opacity .15s' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                          {st === 'done' && <CheckCircleIcon style={{ fontSize: '1rem', color: P.accent2 }} />}
                          {st === 'locked' && <LockIcon style={{ fontSize: '0.9rem', color: P.dim }} />}
                          <span className="lt-title">{t.title}</span>
                        </div>
                        {t.blurb && <div className="lt-blurb">{t.blurb}</div>}
                        {t.requires?.length > 0 && st !== 'done' && (
                          <div className="lt-needs">needs: {t.requires.map(r => titleBySlug[r] || r).join(' · ')}</div>
                        )}
                        {t.lessonMeetingId && <span className="lt-dot" title="Lesson ready" />}
                      </button>
                    )
                  })}
                </div>
              </div>
            ))}
            {isOwner && (
              <div style={{ flex: '0 0 auto', paddingTop: '3rem' }}>
                <button onClick={generateCurriculum} disabled={!!busy} title="Reload the canonical curriculum (upserts topics; keeps everyone's progress)" style={{ background: P.surface, border: `1px dashed ${P.borderDash}`, color: P.muted, borderRadius: 10, padding: '0.6rem 0.9rem', cursor: 'pointer', whiteSpace: 'nowrap' }}>↻ Reload curriculum</button>
              </div>
            )}
          </div>
        )}
      </main>

      {selected && (() => {
        const st = stateOf(selected)
        return (
          <div onClick={() => setSelected(null)} style={{ position: 'absolute', inset: 0, background: P.overlay, display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10 }}>
            <div onClick={e => e.stopPropagation()} style={{ background: P.surface, border: `1px solid ${P.border}`, borderRadius: 16, padding: '1.4rem', maxWidth: 440, width: '90%', boxShadow: `0 20px 60px ${P.overlay}` }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <span style={{ background: `rgba(${P.accentRgb},0.14)`, color: P.accent, borderRadius: 999, padding: '0.1rem 0.6rem', fontSize: '0.72rem', fontWeight: 600 }}>{selected.level}</span>
                <h2 style={{ margin: 0, fontSize: '1.25rem' }}>{selected.title}</h2>
              </div>
              {selected.blurb && <p style={{ color: P.muted, marginTop: '0.5rem' }}>{selected.blurb}</p>}

              {st === 'locked' ? (
                <div style={{ background: P.panel, border: `1px solid ${P.panelBorder}`, borderRadius: 10, padding: '0.8rem', marginTop: '0.8rem', color: P.muted }}>
                  🔒 Locked. Finish first: <strong style={{ color: P.text }}>{(selected.requires || []).filter(r => !completedSlugs.has(r)).map(r => titleBySlug[r] || r).join(', ')}</strong>
                </div>
              ) : (
                <div style={{ display: 'flex', gap: '0.6rem', marginTop: '1.1rem', flexWrap: 'wrap' }}>
                  <button onClick={() => openTopic(selected)} disabled={!!busy} style={{ background: P.accent2, color: P.onAccent, border: 'none', borderRadius: 10, padding: '0.6rem 1.2rem', fontWeight: 600, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '0.3rem' }}>
                    <PlayArrowIcon fontSize="small" /> {selected.lessonMeetingId ? 'Open lesson' : 'Start lesson'}
                  </button>
                  <button onClick={() => openMyPersonal(selected)} disabled={!!busy} title="A private lesson tailored to your profile — only you see it" style={{ background: P.surface2, color: P.accent, border: `1px solid rgba(${P.accentRgb},0.4)`, borderRadius: 10, padding: '0.6rem 1.1rem', cursor: 'pointer' }}>
                    {readPersonal(selected) ? '✦ Open my version' : '✦ Personalise for me'}
                  </button>
                  {isOwner && selected.lessonMeetingId && <button onClick={() => openTopic(selected, true)} disabled={!!busy} title="Owner: rebuild the shared lesson (replaces it for everyone)" style={{ background: P.surface2, color: P.muted, border: `1px solid ${P.border}`, borderRadius: 10, padding: '0.6rem 1.1rem', cursor: 'pointer' }}>↻ Regenerate</button>}
                  {!selected.completed && <button onClick={() => markComplete(selected)} disabled={!!busy} style={{ background: P.surface2, color: P.text, border: `1px solid ${P.border}`, borderRadius: 10, padding: '0.6rem 1.1rem', cursor: 'pointer' }}>Mark complete ✓</button>}
                  {selected.completed && <span style={{ color: P.accent, alignSelf: 'center' }}>✓ Completed</span>}
                  {getBuiltinLesson(code, selected.slug) && <span title="A hand-authored workbook lesson — opens instantly, no model needed" style={{ alignSelf: 'center', fontSize: '0.78rem', color: P.amber }}>✦ authored lesson</span>}
                </div>
              )}
              {busy && <div style={{ color: P.accent, marginTop: '0.8rem', fontSize: '0.85rem' }}>{busy}</div>}
            </div>
          </div>
        )
      })()}
    </div>
  )
}
